<template>
	<div class="calculator-container">
		<div class="calculator-inputs">
			<div class="vx-row">
				<div class="vx-col lg:w-1/2 md:w-1/2 w-full">
					<div class="input-group">
						<label>Verkoopsprijs (incl. btw)</label>
						<div class="input-wrapper">
							<span class="currency">€</span>
							<vs-input
								type="number"
								v-model.number="inputs.sellingPrice"
								@input="calculateProfit"
								step="0.01"
								placeholder="0.00"
							></vs-input>
						</div>
					</div>
				</div>
				<div class="vx-col lg:w-1/2 md:w-1/2 w-full">
					<div class="input-group">
						<label>BTW percentage</label>
						<div class="input-wrapper">
							<span class="currency">%</span>
							<vs-input
								type="number"
								v-model.number="inputs.vat"
								@input="calculateProfit"
								step="0.1"
								placeholder="21"
							></vs-input>
						</div>
					</div>
				</div>
				<div class="vx-col lg:w-1/2 md:w-1/2 w-full">
					<div class="input-group">
						<label>Inkoopsprijs (excl. btw)</label>
						<div class="input-wrapper">
							<span class="currency">€</span>
							<vs-input
								type="number"
								v-model.number="inputs.purchasePrice"
								@input="calculateProfit"
								step="0.01"
								placeholder="0.00"
							></vs-input>
						</div>
					</div>
				</div>
				<div class="vx-col lg:w-1/2 md:w-1/2 w-full">
					<div class="input-group">
						<label>Vaste commissie (excl. btw)</label>
						<div class="input-wrapper">
							<span class="currency">€</span>
							<vs-input
								type="number"
								v-model.number="inputs.fixedCommission"
								@input="calculateProfit"
								step="0.01"
								placeholder="0.00"
							></vs-input>
						</div>
					</div>
				</div>
				<div class="vx-col lg:w-1/2 md:w-1/2 w-full">
					<div class="input-group">
						<label>Variabele commissie (%)</label>
						<div class="input-wrapper">
							<span class="currency">%</span>
							<vs-input
								type="number"
								v-model.number="inputs.variableCommission"
								@input="calculateProfit"
								step="0.01"
								placeholder="0"
							></vs-input>
						</div>
					</div>
				</div>
				<div class="vx-col lg:w-1/2 md:w-1/2 w-full">
					<div class="input-group">
						<label>Verzendkosten (excl. btw)</label>
						<div class="input-wrapper">
							<span class="currency">€</span>
							<vs-input
								type="number"
								v-model.number="inputs.shippingCosts"
								@input="calculateProfit"
								step="0.01"
								placeholder="0.00"
							></vs-input>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Results Section - 70% and Receipt Section - 30% -->
		<div class="results-wrapper">
			<!-- Results Section - 70% -->
			<div class="results-section">
				<div class="results-grid">
					<!-- Row 1: Price breakdown -->
					<div class="result-item">
						<div class="result-label">Verkoopsprijs (excl. btw)</div>
						<div class="result-value">€ {{ formatNumber(calculations.priceExclVat) }}</div>
					</div>

					<div class="result-item">
						<div class="result-label">BTW bedrag</div>
						<div class="result-value">€ {{ formatNumber(calculations.vatAmount) }}</div>
					</div>

					<div class="result-item">
						<div class="result-label">Totale opbrengst</div>
						<div class="result-value highlight">€ {{ formatNumber(calculations.totalRevenue) }}</div>
					</div>

					<!-- Row 2: Costs -->
					<div class="result-item">
						<div class="result-label">Inkoopsprijs</div>
						<div class="result-value negative">€ {{ formatNumber(calculations.purchasePrice) }}</div>
					</div>

					<div class="result-item">
						<div class="result-label">Vaste commissie</div>
						<div class="result-value negative">€ {{ formatNumber(calculations.fixedCommission) }}</div>
					</div>

					<div class="result-item">
						<div class="result-label">Variabele commissie</div>
						<div class="result-value negative">€ {{ formatNumber(calculations.variableCommission) }}</div>
					</div>

					<!-- Row 3: Shipping -->
					<div class="result-item">
						<div class="result-label">Verzendkosten</div>
						<div class="result-value negative">€ {{ formatNumber(calculations.shippingCosts) }}</div>
					</div>

					<!-- Profit Summary Row -->
					<div class="profit-summary">
						<div class="result-item separator">
							<div class="result-label profit-label">Winst / Verlies (excl. btw)</div>
							<div :class="['result-value', 'profit-value', calculations.profit >= 0 ? 'positive' : 'negative']">
								€ {{ formatNumber(calculations.profit) }}
							</div>
						</div>

						<div class="result-item">
							<div class="result-label">Winstmarge (%)</div>
							<div :class="['result-value', 'profit-value', calculations.profitMargin >= 0 ? 'positive' : 'negative']">
								{{ calculations.profitMargin.toFixed(2) }}%
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Receipt Section - 30% -->
			<div class="receipt-section">
				<div class="receipt-header">Berekening</div>

				<div class="receipt-line">
					<span class="receipt-label">Verkoopsprijs (incl. btw)</span>
					<span class="receipt-value">€ {{ formatNumber(calculations.totalRevenue) }}</span>
				</div>

				<div class="receipt-line">
					<span class="receipt-label">- BTW</span>
					<span class="receipt-value negative">€ {{ formatNumber(calculations.vatAmount) }}</span>
				</div>

				<div class="receipt-separator"></div>

				<div class="receipt-line">
					<span class="receipt-label">Opbrengst (excl. btw)</span>
					<span class="receipt-value">€ {{ formatNumber(calculations.priceExclVat) }}</span>
				</div>

				<div class="receipt-line">
					<span class="receipt-label">- Inkoopsprijs</span>
					<span class="receipt-value negative">€ {{ formatNumber(calculations.purchasePrice) }}</span>
				</div>

				<div class="receipt-line">
					<span class="receipt-label">- Vaste commissie</span>
					<span class="receipt-value negative">€ {{ formatNumber(calculations.fixedCommission) }}</span>
				</div>

				<div class="receipt-line">
					<span class="receipt-label">- Variabele commissie</span>
					<span class="receipt-value negative">€ {{ formatNumber(calculations.variableCommission) }}</span>
				</div>

				<div class="receipt-line">
					<span class="receipt-label">- Verzendkosten</span>
					<span class="receipt-value negative">€ {{ formatNumber(calculations.shippingCosts) }}</span>
				</div>

				<div class="receipt-separator"></div>

				<div class="receipt-line receipt-total">
					<span class="receipt-label">Winst / Verlies</span>
					<span :class="['receipt-value', calculations.profit >= 0 ? 'positive' : 'negative']">
						€ {{ formatNumber(calculations.profit) }}
					</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'calculator',
	props: {
		product: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			inputs: {
				sellingPrice: 0,
				vat: 21,
				purchasePrice: 0,
				fixedCommission: 0,
				variableCommission: 0,
				shippingCosts: 0,
			},
			calculations: {
				priceExclVat: 0,
				vatAmount: 0,
				totalRevenue: 0,
				purchasePrice: 0,
				fixedCommission: 0,
				variableCommission: 0,
				shippingCosts: 0,
				profit: 0,
				profitMargin: 0,
			}
		};
	},
	watch: {
		product: {
			handler(newProduct) {
				this.initializeFromProduct(newProduct);
			},
			immediate: true,
			deep: true
		}
	},
	methods: {
		initializeFromProduct(product) {
			if (product && product.id) {
				this.inputs.sellingPrice = parseFloat(product.price) || 0;
				this.inputs.purchasePrice = parseFloat(product.purchase_price) || 0;
				this.inputs.fixedCommission = parseFloat(product.fixed_commission) || 0;
				this.inputs.variableCommission = parseFloat(product.variable_commission) || 0;
				this.inputs.shippingCosts = parseFloat(product.shipment_price) || 0;
				this.inputs.vat = 21;
				this.calculateProfit();
			}
		},
		update() {
			// Update the product object with calculator values
			// Note: sellingPrice (verkoopsprijs) is NOT saved to API as it's read-only
			this.product.purchase_price = this.inputs.purchasePrice;
			this.product.fixed_commission = this.inputs.fixedCommission;
			this.product.variable_commission = this.inputs.variableCommission;
			this.product.shipment_price = this.inputs.shippingCosts;

			return Promise.resolve();
		},
		calculateProfit() {
			const { sellingPrice, vat, purchasePrice, fixedCommission, variableCommission, shippingCosts } = this.inputs;

			// Calculate price excluding VAT
			const priceExclVat = sellingPrice / (1 + vat / 100);
			const vatAmount = sellingPrice - priceExclVat;

			// Calculate variable commission from selling price (incl. VAT) - platforms calculate on total price
			const varCommission = (sellingPrice * variableCommission) / 100;

			// Calculate profit based on selling price excl. VAT (because VAT must be paid to government)
			const profit = priceExclVat - purchasePrice - fixedCommission - varCommission - shippingCosts;

			// Calculate profit margin based on selling price excl. VAT
			const profitMargin = priceExclVat > 0 ? (profit / priceExclVat) * 100 : 0;

			// Update calculations
			this.calculations = {
				priceExclVat: priceExclVat,
				vatAmount: vatAmount,
				totalRevenue: sellingPrice,
				purchasePrice: purchasePrice,
				fixedCommission: fixedCommission,
				variableCommission: varCommission,
				shippingCosts: shippingCosts,
				profit: profit,
				profitMargin: profitMargin,
			};
		},
		formatNumber(value) {
			if (typeof value !== 'number') return '0.00';
			return value.toFixed(2);
		}
	}
};
</script>

<style scoped>
.calculator-container {
	display: flex;
	flex-direction: column;
	gap: 32px;
}

.calculator-inputs {
	background: #f8f9fa;
	padding: 24px;
	border-radius: 8px;
	border: 1px solid #e9ecef;
}

body.theme-dark .calculator-inputs {
	background: #262626;
	border-color: #3a3a3a;
}

body.theme-semi-dark .calculator-inputs {
	background: #323232;
	border-color: #404040;
}

.input-group {
	margin-bottom: 0;
}

.input-group label {
	display: block;
	font-size: 12px;
	font-weight: 600;
	color: #666;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 8px;
	transition: color 0.3s ease;
}

body.theme-dark .input-group label,
body.theme-semi-dark .input-group label {
	color: #aaa;
}

.input-wrapper {
	position: relative;
	display: flex;
	align-items: center;
}

.currency {
	position: absolute;
	left: 12px;
	font-weight: 600;
	color: #666;
	pointer-events: none;
	transition: color 0.3s ease;
}

body.theme-dark .currency,
body.theme-semi-dark .currency {
	color: #aaa;
}

.input-wrapper .vs-input {
	padding-left: 28px;
	flex: 1;
}

.suffix {
	position: absolute;
	right: 12px;
	font-weight: 600;
	color: #666;
	pointer-events: none;
	transition: color 0.3s ease;
}

body.theme-dark .suffix,
body.theme-semi-dark .suffix {
	color: #aaa;
}

.input-wrapper .vs-input input {
	padding-left: 28px;
}

/* Results Wrapper */
.results-wrapper {
	display: flex;
	gap: 20px;
	align-items: flex-start;
	padding: 0 24px 0px 0px;
}

/* Results Section */
.results-section {
	flex: 0 0 70%;
	background: linear-gradient(135deg, #f5f7fa 0%, #fafbfc 100%);
	padding: 32px;
	border-radius: 8px;
	border: 1px solid #e9ecef;
}

body.theme-dark .results-section {
	background: linear-gradient(135deg, #2a2a2a 0%, #262626 100%);
	border-color: #3a3a3a;
}

body.theme-semi-dark .results-section {
	background: linear-gradient(135deg, #323232 0%, #2e2e2e 100%);
	border-color: #404040;
}

.results-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
	gap: 20px;
}

.result-item {
	background: white;
	padding: 16px;
	border-radius: 6px;
	border: 1px solid #e9ecef;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	transition: all 0.2s ease;
}

body.theme-dark .result-item {
	background: #1e1e1e;
	border-color: #3a3a3a;
	color: #e0e0e0;
}

body.theme-semi-dark .result-item {
	background: #2a2a2a;
	border-color: #404040;
	color: #e0e0e0;
}

.result-item.separator {
	border-top: 2px solid #667eea;
	padding-top: 16px;
}

.profit-summary {
	grid-column: 1/-1;
	display: flex;
	gap: 20px;
}

.profit-summary .result-item {
	flex: 1;
}

/* Receipt Section */
.receipt-section {
	flex: 0 0 30%;
	background: linear-gradient(135deg, #f5f7fa 0%, #fafbfc 100%);
	padding: 32px;
	border-radius: 8px;
	border: 1px solid #e9ecef;
	font-family: 'Courier New', monospace;
	font-size: 12px;
	line-height: 1.8;
	height: fit-content;
}

body.theme-dark .receipt-section {
	background: linear-gradient(135deg, #2a2a2a 0%, #262626 100%);
	border-color: #3a3a3a;
	color: #e0e0e0;
}

body.theme-semi-dark .receipt-section {
	background: linear-gradient(135deg, #323232 0%, #2e2e2e 100%);
	border-color: #404040;
	color: #e0e0e0;
}

.receipt-header {
	text-align: center;
	font-weight: 700;
	margin-bottom: 12px;
	padding-bottom: 8px;
	border-bottom: 2px dashed #ccc;
	color: #333;
}

body.theme-dark .receipt-header,
body.theme-semi-dark .receipt-header {
	color: #e0e0e0;
	border-bottom-color: #555;
}

.receipt-line {
	display: flex;
	justify-content: space-between;
	gap: 8px;
	margin-bottom: 4px;
}

.receipt-label {
	flex: 1;
	text-align: left;
	color: #666;
}

body.theme-dark .receipt-label,
body.theme-semi-dark .receipt-label {
	color: #aaa;
}

.receipt-value {
	text-align: right;
	white-space: nowrap;
	font-weight: 600;
	color: #333;
}

body.theme-dark .receipt-value,
body.theme-semi-dark .receipt-value {
	color: #e0e0e0;
}

.receipt-value.negative {
	color: #dc3545;
}

.receipt-value.positive {
	color: #28a745;
}

.receipt-separator {
	border-top: 1px dashed #ccc;
	margin: 8px 0;
}

body.theme-dark .receipt-separator,
body.theme-semi-dark .receipt-separator {
	border-top-color: #555;
}

.receipt-line.receipt-total {
	font-weight: 700;
	margin-top: 8px;
	padding-top: 8px;
	border-top: 2px solid #333;
}

body.theme-dark .receipt-line.receipt-total,
body.theme-semi-dark .receipt-line.receipt-total {
	border-top-color: #e0e0e0;
}

.result-label {
	font-size: 12px;
	font-weight: 600;
	color: #999;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 8px;
	transition: color 0.3s ease;
}

body.theme-dark .result-label,
body.theme-semi-dark .result-label {
	color: #888;
}

.profit-label {
	color: #667eea;
	font-size: 13px;
}

.result-value {
	font-size: 18px;
	font-weight: 700;
	color: #333;
	transition: color 0.3s ease;
}

body.theme-dark .result-value,
body.theme-semi-dark .result-value {
	color: #e0e0e0;
}

.result-value.highlight {
	color: #667eea;
	font-size: 20px;
}

.result-value.negative {
	color: #dc3545;
}

.result-value.positive {
	color: #28a745;
}

body.theme-dark .result-value.negative,
body.theme-semi-dark .result-value.negative {
	color: #ff6b6b;
}

body.theme-dark .result-value.positive,
body.theme-semi-dark .result-value.positive {
	color: #51cf66;
}

.profit-value {
	font-size: 24px;
	font-weight: 800;
}

/* Vuesax overrides */
body.theme-dark .calculator-inputs .vs-input input,
body.theme-semi-dark .calculator-inputs .vs-input input {
	color: #e0e0e0 !important;
	background: rgba(255, 255, 255, 0.05) !important;
	border-color: #3a3a3a !important;
}

body.theme-dark .calculator-inputs .vs-input input::placeholder,
body.theme-semi-dark .calculator-inputs .vs-input input::placeholder {
	color: #888 !important;
}

/* Responsive */
@media (max-width: 1024px) {
	.results-wrapper {
		flex-direction: column;
	}

	.results-section {
		flex: 1 1 100%;
	}

	.receipt-section {
		flex: 1 1 100%;
		height: auto;
	}
}

@media (max-width: 768px) {
	.results-grid {
		grid-template-columns: 1fr;
	}

	.profit-summary {
		flex-direction: column;
	}

	.profit-summary .result-item {
		flex: 1 1 100%;
	}
}
</style>
